import {useEffect, useRef} from 'react';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';

import {watchPushMessages} from '@/shared/push';
import {toast} from '@/shared/ui/toast';

/**
 * Что кабинет делает с пришедшим пушем.
 *
 * Сервер шлёт сотруднику уведомление о его СОБСТВЕННОМ проходе («Приход» / «Уход»),
 * поэтому единственное место, куда пуш ведёт, — «Мои проходы». Этой вкладки нет у
 * персоны компании: владелец, которому пришёл пуш как сотруднику, получает тост без
 * кнопки, и тап просто открывает приложение, а не роняет навигацию.
 *
 * - **Приложение на экране** — система ничего не рисует (шторка только для фона),
 *   поэтому пуш показывается тостом.
 * - **Тап по уведомлению** — из фона и с холодного старта — ведёт на вкладку.
 *
 * Монтируется в `MainTabs`: там уже есть сессия и навигатор, на которые можно действовать.
 */
const PUSH_TARGET = 'MyLogs';

export function usePushMessages(tabNames: readonly string[]) {
  const {t} = useTranslation();
  const navigation = useNavigation();

  // Подписка — на всё время жизни оболочки, а набор вкладок меняется вместе с
  // персоной: последний читается через ref, чтобы не переподписываться на каждую смену.
  const canOpen = useRef(false);
  canOpen.current = tabNames.includes(PUSH_TARGET);

  useEffect(() => {
    // Вкладочный навигатор без статического списка параметров (см. `tabsFor`) —
    // переход по имени через приведение, как в EmployeeReportScreen.
    const open = () => {
      if (!canOpen.current) {
        return;
      }
      (navigation as unknown as {navigate: (route: string, params?: object) => void}).navigate(
        'Main',
        {screen: PUSH_TARGET},
      );
    };

    return watchPushMessages({
      onForeground: message => {
        toast.info(message.title ?? t('push.receivedTitle'), {
          message: message.body ?? undefined,
          action: canOpen.current ? {label: t('push.open'), onPress: open} : undefined,
        });
      },
      onOpen: open,
    });
  }, [navigation, t]);
}
